import { Suspense, use, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useAccountStore } from '@/stores/account-store'
import { edgeUsersApi } from '@/services/api/users-api'
import { LoadingScreen } from '@/components/common/loading-screen'
import { setIdbUserScope } from '@/workspace/idb-cache'
import { storageScopeId } from '@/services/auth/types'
import type { AuthSession } from '@/services/auth/types'
import { ensureMigratedUserProvisioned } from '@/services/auth/provision-migration'
import { bootIntercom, shutdownIntercom } from '@/services/intercom'
import { identifyCustomerIo, resetCustomerIo } from '@/services/customerio'
import { initTickerDirectory, destroyTickerDirectory } from '@/stores/ticker-directory'
import { edgeDataApi } from '@/services/api/data-api'
import { store } from '@/store'

/** Refresh tokens this far before they expire so consumers never see a stale token. */
const REFRESH_BUFFER_MS = 2 * 60 * 1000

/** Decode the `exp` (seconds since epoch) claim from a JWT, or null if it can't be parsed. */
function readJwtExpMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Initializes auth state on app load and proactively refreshes tokens
 * before they expire.
 *
 * The refresh path calls the store's `refreshSession` ACTION, which force-refreshes the
 * tokens AND updates `useAccountStore.session` — the single source of truth every token
 * consumer reads (the request layer, `companionAuthToken`). Critically it sets ONLY
 * `session`, never `isAuthenticating`: flipping that (as `restoreSession()` does) makes
 * `billing-guard`/`AuthGuard` render a LoadingScreen and unmount the entire authenticated
 * subtree every refresh cycle. So refresh via the store action, never `restoreSession()`
 * or a bare `authService.refreshSession()`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const userSub = useAccountStore((s) => s.session?.user?.sub)
  const scopeId = useAccountStore((s) =>
    s.session?.user ? storageScopeId(s.session.user) : undefined,
  )
  const session = useAccountStore((s) => s.session)
  const [initialized, setInitialized] = useState(false)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Restore session from storage on mount
  useEffect(() => {
    useAccountStore
      .getState()
      .restoreSession()
      .finally(() => setInitialized(true))
  }, [])

  // Proactive token refresh — fires shortly before the access token's exp.
  useEffect(() => {
    if (!userSub) {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
      return
    }

    function scheduleNextRefresh() {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      const token = useAccountStore.getState().session?.tokens.accessToken
      if (!token) return
      const expMs = readJwtExpMs(token)
      if (expMs == null) return
      // If the token is already past the refresh window, fire immediately.
      const delay = Math.max(expMs - REFRESH_BUFFER_MS - Date.now(), 0)
      refreshTimerRef.current = setTimeout(async () => {
        const ok = await useAccountStore.getState().refreshSession()
        if (ok) scheduleNextRefresh()
        // If refresh fails the base-query 401 path will retry on the next
        // request and surface a real auth failure if the refresh token is
        // genuinely dead. Don't loop here.
      }, delay)
    }

    scheduleNextRefresh()
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [userSub])

  // Scope IDB workspace cache to the stable edgeUserId
  useEffect(() => {
    if (scopeId) {
      setIdbUserScope(scopeId)
    }
  }, [scopeId])

  // Only block while restoring the auth session (instant with MockAuthService).
  if (!initialized) {
    return <LoadingScreen message="Loading..." />
  }
  // No session → the app renders its public routes (login, etc.) via the route guards.
  if (!session) {
    return <>{children}</>
  }
  // Authenticated: everything that needs the identity row lives in AuthedApp, gated by a
  // Suspense boundary so a migrated user is held on the splash until the row is written.
  return (
    <Suspense fallback={<LoadingScreen message="Preparing your account…" />}>
      <AuthedApp session={session}>{children}</AuthedApp>
    </Suspense>
  )
}

/**
 * Session-scoped bootstrap: profile fetch, ticker directory, Intercom and Customer.io.
 * Mounted only when authenticated. A migrated user (the id token carries `custom:migration_stage`)
 * suspends here on a one-time `use()` until their edge-identity row is provisioned from
 * the id token, so no strict access-token call fires before the row exists. That single
 * write is the whole migration, so the splash is brief and needs no polling. New sessions
 * never suspend — `use()` isn't reached.
 */
function AuthedApp({ session, children }: { session: AuthSession; children: ReactNode }) {
  if (session.user.migrationStage) {
    use(ensureMigratedUserProvisioned(session.user.sub, session.tokens.idToken))
  }

  // Never blocking: the app works without the profile, and the API may be down locally.
  // Focus refetch: a plan change made in the Stripe portal or another tab lands on the
  // user row, and the plan gates read it from here.
  const { data: fetchedUser } = edgeUsersApi.useGetUserQuery(undefined, { refetchOnFocus: true })

  useEffect(() => {
    if (fetchedUser) useAccountStore.getState().setUser(fetchedUser)
  }, [fetchedUser])

  useEffect(() => {
    if (fetchedUser) bootIntercom(fetchedUser, session.user.email)
  }, [fetchedUser, session.user.email])
  useEffect(() => () => shutdownIntercom(), []) // shut down on logout (unmount)

  // Customer.io in-app messages, on the backend's profile id; reset on logout (unmount).
  useEffect(() => {
    identifyCustomerIo(session.user.edgeUserId)
    return () => resetCustomerIo()
  }, [session.user.edgeUserId])

  // Load the full ticker directory for instant client-side search.
  useEffect(() => {
    initTickerDirectory(async () => {
      const result = await store.dispatch(
        edgeDataApi.endpoints.listTickers.initiate(undefined, { forceRefetch: true }),
      )
      return result.data ?? []
    })
    return () => destroyTickerDirectory()
  }, [])

  return <>{children}</>
}
